import type { NewsItem, CampaignItem, EventItem, BenefitItem } from './joc-club-admin';

/**
 * User-facing read-only queries for JOC Club content.
 *
 * Calls the public server-side API routes (`/api/joc-club/*`), which return
 * only published/active items. The browser never talks to KV directly.
 */

const NEWS_DATE_FIELDS = ['publishedAt', 'createdAt'];
const CAMPAIGNS_DATE_FIELDS = ['startAt', 'endAt', 'createdAt'];
const EVENTS_DATE_FIELDS = ['eventDate', 'createdAt'];
const BENEFITS_DATE_FIELDS = ['createdAt'];

function reviveDates<T>(data: Record<string, unknown>, dateFields: string[]): T {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    result[key] = dateFields.includes(key) && typeof value === 'string' ? new Date(value) : value;
  }
  return result as T;
}

async function fetchPublished<T>(resource: string, dateFields: string[]): Promise<T[]> {
  const res = await fetch(`/api/joc-club/${resource}`);
  if (!res.ok) {
    throw new Error(`Failed to load ${resource}: ${res.status}`);
  }
  const items = (await res.json()) as Record<string, unknown>[];
  return items.map((i) => reviveDates<T>(i, dateFields));
}

export function getPublishedNews(): Promise<NewsItem[]> {
  return fetchPublished<NewsItem>('news', NEWS_DATE_FIELDS);
}

export function getActiveCampaigns(): Promise<CampaignItem[]> {
  return fetchPublished<CampaignItem>('campaigns', CAMPAIGNS_DATE_FIELDS);
}

export function getUpcomingEvents(): Promise<EventItem[]> {
  return fetchPublished<EventItem>('events', EVENTS_DATE_FIELDS);
}

export function getPublishedBenefits(): Promise<BenefitItem[]> {
  return fetchPublished<BenefitItem>('benefits', BENEFITS_DATE_FIELDS);
}
